import { apiClient } from '@/services/api';
import { parseApiError } from '@/services/api/api.errors';
import { authMocks, extractUserIdFromToken } from './auth/auth.mocks';
import { TokenManager } from './auth/token-manager';
import { clientHelper } from '@/services/api/client-helper';
import type { 
  LoginData, 
  AuthResponse, 
  TokenRefreshResponse,
  ProfileUpdateData 
} from '@/types/auth.types';
import type { User } from '@/types/user';

class AuthService {
  private tokenManager = new TokenManager();
  private useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true' || import.meta.env.VITE_USE_MOCK_DATA === 'true';
  private currentUser: User | null = null;

  constructor() {
    // Set up refresh callback
    this.tokenManager.setRefreshCallback(async (refreshToken: string) => {
      const response = await this.refreshToken(refreshToken);
      return {
        token: response.token,
        refreshToken: response.refreshToken
      };
    });
  }

  async login(data: LoginData): Promise<AuthResponse> {
    try {
      if (this.useMockAuth) {
        const response = await authMocks.login(data.email, data.password);
        
        if (response.success && response.token && response.refreshToken) {
          this.tokenManager.setTokens(response.token, response.refreshToken);
        }
        
        return response;
      }

      const response = await apiClient.post<any>('/auth/login', data);
      
      // Handle the actual API response structure
      if (response.data.success && response.data.data) {
        const { user, session } = response.data.data;
        
        if (session?.accessToken && session?.refreshToken) {
          this.tokenManager.setTokens(
            session.accessToken,
            session.refreshToken
          );
        }
        
        // Map "viewer" role to "client" for frontend consistency
        const userRole = user.role === 'viewer' ? 'client' : user.role;
        
        // Transform the response to match expected structure
        const transformedUser: User = {
          id: user.id,
          email: user.email,
          role: userRole as 'admin' | 'developer' | 'client',
          name: user.name || user.email.split('@')[0], // Use email prefix as name if not provided
          avatar_url: user.avatar_url || null,
          client_project_id: user.client_project_id || null, // Include client_project_id from login response
          is_profile_complete: true, // API doesn't provide this, assume true
          created_at: user.created_at || new Date().toISOString(),
          updated_at: user.lastSignIn || new Date().toISOString()
        };
        
        // Store the user data for later getProfile calls
        this.currentUser = transformedUser;
        
        // After successful login, fetch complete user profile with client_project_id
        try {
          const profileResponse = await apiClient.get<any>('/auth/me');
          if (profileResponse.data.success && profileResponse.data.data) {
            const meData = profileResponse.data.data;
            
            // Update stored user with data from /me endpoint
            this.currentUser = {
              ...transformedUser,
              id: meData.id || transformedUser.id,
              name: meData.fullName || meData.name || transformedUser.name,
              client_project_id: meData.clientProjectId || meData.client_project_id || transformedUser.client_project_id,
              avatar_url: meData.avatarUrl || meData.avatar_url || transformedUser.avatar_url
            };
          }
        } catch (meError) {
          console.warn('Failed to fetch /auth/me after login, using login response data:', meError);
        }
        
        return {
          success: true,
          token: session.accessToken,
          refreshToken: session.refreshToken,
          user: this.currentUser
        };
      }
      
      throw new Error('Invalid response structure from API');
    } catch (error) {
      throw parseApiError(error);
    }
  }


  async logout(): Promise<void> {
    try {
      const token = this.tokenManager.getAccessToken();
      
      if (!this.useMockAuth && token) {
        // Use Bearer token in Authorization header instead of sending refreshToken in body
        await apiClient.post('/auth/logout');
      } else if (this.useMockAuth) {
        const refreshToken = this.tokenManager.getRefreshToken();
        if (refreshToken) {
          await authMocks.logout(refreshToken);
        }
      }
    } catch (error) {
      // Log error but don't throw - we want to clear tokens regardless
      console.error('Logout error:', error);
    } finally {
      this.tokenManager.clearTokens();
      this.currentUser = null;
      clientHelper.clearCache(); // Clear client cache on logout
    }
  }

  async refreshToken(refreshToken: string): Promise<TokenRefreshResponse> {
    try {
      if (this.useMockAuth) {
        return await authMocks.refresh(refreshToken);
      }

      const response = await apiClient.post<any>('/auth/refresh', {
        refreshToken
      });
      
      // Handle the actual API response structure
      if (response.data.success && response.data.data?.session) {
        const { session } = response.data.data;
        return {
          token: session.accessToken,
          refreshToken: session.refreshToken,
          expiresIn: session.expiresIn
        };
      }
      
      // Fallback to direct response if structure is different
      return response.data;
    } catch (error) {
      // Clear tokens on refresh failure
      this.tokenManager.clearTokens();
      throw parseApiError(error);
    }
  }

  async getProfile(): Promise<User> {
    try {
      if (this.useMockAuth) {
        const token = this.tokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        
        const userId = extractUserIdFromToken(token);
        if (!userId) {
          throw new Error('Invalid auth token');
        }
        
        return await authMocks.getProfile(userId);
      }

      // Try to get fresh user data from /auth/me endpoint
      try {
        const response = await apiClient.get<any>('/auth/me');
        
        if (response.data.success && response.data.data) {
          const user = response.data.data;
          
          // Map "viewer" role to "client" for frontend consistency
          const userRole = user.role === 'viewer' ? 'client' : user.role;
          
          // Transform the response to match expected User type
          const transformedUser: User = {
            id: user.id,
            email: user.email,
            role: userRole as 'admin' | 'developer' | 'client',
            name: user.fullName || user.name || user.email.split('@')[0],
            avatar_url: user.avatarUrl || user.avatar_url || null,
            client_project_id: user.clientProjectId || user.client_project_id || null, // Handle both camelCase and snake_case
            is_profile_complete: user.emailConfirmed || true,
            created_at: user.createdAt || user.created_at || new Date().toISOString(),
            updated_at: user.updatedAt || user.updated_at || new Date().toISOString()
          };
          
          // Cache the user data
          this.currentUser = transformedUser;
          return transformedUser;
        }
      } catch (error: any) {
        // If /auth/me fails, fall back to cached data
        console.warn('Failed to fetch user from /auth/me:', error.message);
      }

      // Return cached user data from login if available
      if (this.currentUser) {
        return this.currentUser;
      }
      
      // If no cached user data, try to decode from token
      const token = this.tokenManager.getAccessToken();
      if (!token) {
        throw new Error('No auth token available');
      }
      
      // Try to parse user info from JWT token
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          
          // Map "viewer" role to "client" for frontend consistency
          const tokenRole = payload.role === 'viewer' ? 'client' : (payload.role || 'client');
          
          // Return basic user info from token
          return {
            id: payload.sub || payload.id || 'unknown',
            email: payload.email || 'unknown@tendencia.ai',
            role: tokenRole as 'admin' | 'developer' | 'client',
            name: payload.name || payload.email?.split('@')[0] || 'Usuario',
            avatar_url: null,
            client_project_id: payload.client_project_id || null,
            is_profile_complete: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }
      } catch (e) {
        console.error('Failed to parse user from token:', e);
      }
      
      throw new Error('No user profile available');
    } catch (error) {
      throw parseApiError(error);
    }
  }

  async updateProfile(data: ProfileUpdateData): Promise<User> {
    try {
      if (this.useMockAuth) {
        const token = this.tokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        
        const userId = extractUserIdFromToken(token);
        if (!userId) {
          throw new Error('Invalid auth token');
        }
        
        return await authMocks.updateProfile(userId, data);
      }

      const response = await apiClient.put<{ data: User }>('/auth/profile', data);
      return response.data.data;
    } catch (error) {
      throw parseApiError(error);
    }
  }

  // Test endpoints
  async testAuth(): Promise<any> {
    try {
      const response = await apiClient.get('/auth/test');
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  }

  async testAdminAuth(): Promise<any> {
    try {
      const response = await apiClient.get('/auth/admin-test');
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  }

  // Token management methods
  getAccessToken(): string | null {
    return this.tokenManager.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokenManager.getRefreshToken();
  }

  isTokenExpired(): boolean {
    return this.tokenManager.isTokenExpired();
  }

  clearTokens(): void {
    this.tokenManager.clearTokens();
    this.currentUser = null;
    clientHelper.clearCache(); // Clear client cache
  }
  
  // Get current cached user
  getCurrentUser(): User | null {
    return this.currentUser;
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing or custom instances
export { AuthService };